import type { GraphQLErrorExtensions, GraphQLFormattedError } from 'graphql'
import type { DUPLICATE_FILTER_ERROR_CODES } from './duplicate-filter.js'

export type ErrCode =
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'bad_request'
  | 'internal_server_error'
  | 'unknown_error'
  | 'bad_order'
  | 'cell_update_failed'
  | 'chart_configuration_error'
  | 'chart_aggregation_error'
  | 'form_not_shareable'
  | 'form_schema_invalid'
  | 'form_submission_invalid'
  | (typeof DUPLICATE_FILTER_ERROR_CODES)[keyof typeof DUPLICATE_FILTER_ERROR_CODES]

export const ERR_CODE_DEFAULT_STATUS: Partial<Record<ErrCode, number>> = {
  bad_request: 400,
  unauthorized: 401,
  forbidden: 403,
  not_found: 404,
}

export interface DossGQLExtensions extends GraphQLErrorExtensions {
  errCode: ErrCode
  /** Whether the caller caused the failure. Sentry uses this to suppress user errors. */
  isUserError: boolean
  /** Whether the failure was unexpected. Read by workflows and System Events. */
  isException: boolean
}

/** Params are JSON scalars so the same values survive runtime boundaries. */
export type FailureParam = string | number | boolean | null

/**
 * Open string, number and bigint parts accept a numeric template type; literal characters don't.
 * Tail recursion keeps long templates within TypeScript's instantiation limits.
 */
type IsLiteral<T extends string> = T extends `${infer First}${infer Rest}`
  ? Extract<`${number}` | `${bigint}`, First> extends never
    ? IsLiteral<Rest>
    : false
  : T extends ''
    ? true
    : false

/** Reject widened strings and open template-literal types, including interpolated strings. */
type LiteralTemplate<T extends string> = IsLiteral<T> extends true ? T : never

/** One param per %s; %% renders a literal percent sign. */
export type FailureParams<T extends string> = string extends T
  ? readonly FailureParam[]
  : T extends `${string}%${infer Rest}`
    ? Rest extends `%${infer Tail}`
      ? FailureParams<Tail>
      : Rest extends `s${infer Tail}`
        ? readonly [FailureParam, ...FailureParams<Tail>]
        : FailureParams<Rest>
    : readonly []

export interface DossErrorOptions extends ErrorOptions {
  userFault?: boolean
  /** Omit to inherit an explicit DossError cause policy, otherwise keep the runtime's defaults. */
  terminal?: boolean
  transportStatus?: number
  api?: Partial<DossGQLExtensions>
}

export type TemplatedDossErrorArguments<T extends string> = [
  template: LiteralTemplate<T>,
  params: FailureParams<NoInfer<T>>,
  options?: DossErrorOptions,
]

export type DossApiErrorArguments<T extends string> = TemplatedDossErrorArguments<T>

function renderTemplate(template: string, params: readonly FailureParam[]): string {
  let index = 0
  const message = template.replace(/%%|%s/g, (placeholder) => {
    if (placeholder === '%%') {
      return '%'
    }
    if (index >= params.length) {
      throw new TypeError('Missing DossError template param')
    }
    return String(params[index++])
  })
  if (index !== params.length) {
    throw new TypeError('Unexpected DossError template param')
  }
  return message
}

/** A stable failure reason with a readable message. Use '%s' for pass-through text. */
export class DossError<const T extends string = string> extends Error {
  readonly template: string
  readonly templateParams: readonly FailureParam[]
  readonly terminal: boolean | undefined
  readonly transportStatus: number | undefined
  extensions: DossGQLExtensions

  constructor(...[template, params, options = {}]: TemplatedDossErrorArguments<T>) {
    super(renderTemplate(template, params), options)
    this.name = new.target.name
    this.template = template
    this.templateParams = [...params]
    this.terminal = options.terminal
    this.transportStatus = options.transportStatus
    this.extensions = {
      errCode: 'unknown_error',
      isUserError: false,
      isException: true,
      ...options.api,
      ...(options.userFault === undefined ? {} : { isUserError: options.userFault }),
    }
  }

  get userFault(): boolean {
    return this.extensions.isUserError
  }

  /** Preserve the string and JSON representations existing GraphQL-derived errors exposed. */
  override toString(): string {
    return this.message
  }

  toJSON(): GraphQLFormattedError {
    return { message: this.message, extensions: this.extensions }
  }
}
